import { useState } from 'react';
import { useRouter } from 'next/router';
import { planFromPkgName } from '@helpers/tracking';

import './CoursePackagesOverview.scss';
import { cn } from '@helpers/styles';
import icons from '@assets/icons/redesign';
import LinkWithTracking from '../../LinkWithTracking/LinkWithTracking';

const { Checkmark, Cross, ArrowUp, ArrowDown } = icons;

const allCourses = [
  {
    label: 'Zwangerschap',
    link: '/zwangerschapscursus/zwangerschapscursus/introductievideo'
  },
  {
    label: 'Bevalling',
    link: '/zwangerschapscursus/bevallingscursus/introductievideobevalingscursus'
  },
  {
    label: 'Borstvoeding',
    link: '/zwangerschapscursus/borstvoedingscursus/introductievideo'
  },
  {
    label: 'Voeding',
    link: '/zwangerschapscursus/voedingscursus/introductievideo'
  },
  {
    label: 'Sporten en bewegen',
    link: '/zwangerschapscursus/sporten-en-bewegen/introductievideo'
  },
  {
    label: 'Hypnobirthing',
    link: '/zwangerschapscursus/hypnobirthing/introductievideo'
  },
  {
    label: 'Zwangerschapsyoga',
    link: '/zwangerschapscursus/zwangerschapsyoga/introductievideo'
  },
  {
    label: 'Mindfulness',
    link: '/zwangerschapscursus/mindfulnesscursus/introductievideo'
  },
  {
    label: 'Seksualiteit',
    link: '/zwangerschapscursus/seksualiteit-tijdens-en-na-de-zwangerschap/introductievideo'
  },
  {
    label: 'Herstel na de bevalling',
    link: '/zwangerschapscursus/herstel-na-de-bevalling-sep/introductievideo'
  },
  {
    label: 'Babymassage',
    link: '/zwangerschapscursus/babymassage/introductievideo'
  },
  {
    label: 'Dragen en hechting',
    link: '/zwangerschapscursus/dragen-en-hechting/introductievideo'
  },
  {
    label: 'Slaapcursus 0-6 maanden',
    link: '/zwangerschapscursus/slaapcursus0-6maanden/introductievideo'
  },
  {
    label: 'Baby- en kindergebaren',
    link: '/zwangerschapscursus/babysignalen-en-baby-en-kinder-gebaren/introductievideo'
  },
  {
    label: 'Mindfulness na de bevalling',
    link: '/zwangerschapscursus/mindfulness-na-bevalling/introductievideo'
  },
  {
    label: 'De eerste 100 dagen na de bevalling',
    link: '/zwangerschapscursus/de-eerste-100-dagen-na-de-bevalling/introductievideo'
  },
  {
    label: 'Slaapcursus 0,5-5 jaar',
    link: '/zwangerschapscursus/slaapcursus-pakket/introductievideo'
  }
];

const packages = [
  {
    title: 'Basispakket',
    name: 'online-cursussen-basis',
    oldPrice: '149,95',
    newPrice: '99,95',
    description:
      'De belangrijkste cursussen voor je zwangerschap en bevalling.',
    features: [
      {
        label: '8 cursussen',
        items: markIncluded(8)
      },
      { label: '12 maanden toegang' },
      { label: 'Gegeven door experts' },
      { label: 'Vaak (deels) vergoed*' }
    ],
    highlight: false
  },
  {
    title: 'Pluspakket',
    name: 'online-cursussen-plus',
    oldPrice: '249,95',
    newPrice: '139,95',
    description:
      'Alle cursussen voor je zwangerschap, bevalling en het ouderschap.',
    features: [
      {
        label: '17 cursussen',
        items: markIncluded(17)
      },
      { label: '18 maanden toegang' },
      { label: 'Gegeven door experts' },
      { label: 'Vaak (deels) vergoed*' }
    ],
    highlight: 'Meest gekozen'
  },
  {
    title: 'Pro-pakket',
    name: 'online-cursussen-pro',
    oldPrice: '299,95',
    newPrice: '179,95',
    description: 'Extra lang toegang tot alle cursussen van cursusportaal.',
    features: [
      {
        label: '17 cursussen',
        items: markIncluded(17)
      },
      { label: '36 maanden toegang' },
      { label: 'Gegeven door experts' },
      { label: 'Vaak (deels) vergoed*' }
    ],
    highlight: false
  }
];

export default function CoursePackagesOverview() {
  const router = useRouter();
  const [expandedFeature, setExpandedFeature] = useState({});

  const { dimension6, dimension7 } = router.query;
  const packagesToRender = adjustPackagesByQuery(packages, dimension6, dimension7);

  const toggleFeature = (pkgIdx, featureIdx) => {
    const key = `${pkgIdx}-${featureIdx}`;
    setExpandedFeature((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <section className="coursePackages">
      <h2 id="cursuspakketten" className="coursePackages-title">
        Cursuspakketten
      </h2>
      <p className="coursePackages-subtitle">
        Kies het cursuspakket dat bij jou past
      </p>
      <div
        className={cn('coursePackages-list', {
          'coursePackages-list-collapsed': Object.values(expandedFeature).every(
            (v) => !v
          )
        })}
      >
        {packagesToRender.map((pkg, idx) => (
          <div
            key={idx}
            className={cn('coursePackages-card', {
              'coursePackages-card-highlighted': pkg.highlight,
              'coursePackages-card-open': pkg.features.some(
                (_, i) => expandedFeature[`${idx}-${i}`]
              )
            })}
          >
            <h3 className="coursePackages-name">{pkg.title}</h3>
            {pkg.highlight && (
              <div className="coursePackages-tag">{pkg.highlight}</div>
            )}
            <div className="coursePackages-price">
              <span className="coursePackages-price-old">€{pkg.oldPrice}</span>
              <span className="coursePackages-price-new">€{pkg.newPrice}</span>
            </div>
            <p className="coursePackages-description">{pkg.description}</p>
            <div className="coursePackages-bottom">
              <LinkWithTracking
                href={`/zwangerschapscursus/afrekenen?package=${pkg.name}`}
                className="coursePackages-button"
                category="CTA Bestel"
                action={
                  planFromPkgName(pkg.name)
                    ? `Bestel ${planFromPkgName(pkg.name)} Plan`
                    : null
                }
                name="Lander 1"
              >
                Bestel cursuspakket
              </LinkWithTracking>

              <LinkWithTracking
                href="/verzekering/cursus-vergoeding"
                className="coursePackages-note"
                target="_blank"
                category="welkom-lander"
                action="click-check-vergoeding"
                name={pkg.name}
              >
                *Check of jouw zorgverzekering onze cursussen vergoed &gt;
              </LinkWithTracking>
            </div>

            <ul className="coursePackages-feature-list">
              {pkg.features.map((feature, i) => {
                const key = `${idx}-${i}`;
                const isOpen = !!expandedFeature[key];

                return (
                  <li
                    key={i}
                    className="coursePackages-feature-list-item"
                    onClick={() => feature.items && toggleFeature(idx, i)}
                    style={{ cursor: feature.items ? 'pointer' : 'default' }}
                  >
                    <div className="coursePackages-feature-list-item-content">
                      <Checkmark className="coursePackages-checkmark-icon" />
                      {feature.label}
                      {feature.items && (
                        <span className="coursePackages-expand-toggle">
                          {isOpen ? <ArrowUp /> : <ArrowDown />}
                        </span>
                      )}
                    </div>

                    {feature.items && (
                      <ul
                        className={cn('coursePackages-feature-sublist', {
                          'coursePackages-feature-sublist-collapsed': !isOpen
                        })}
                      >
                        {feature.items.map((sub, i) => (
                          <li
                            key={i}
                            className="coursePackages-feature-sublist-item"
                          >
                            {sub.included ? (
                              <Checkmark className="coursePackages-checkmark-icon icon" />
                            ) : (
                              <Cross className="coursePackages-crossmark-icon icon" />
                            )}
                            <a href={sub.link} target="_blank">
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function markIncluded(includedCount) {
  return allCourses.map((course, idx) => ({
    ...course,
    included: idx < includedCount
  }));
}

function adjustPackagesByQuery(packages, dimension6, dimension7) {
  const plus = packages.find((p) => p.name === 'online-cursussen-plus');

  if (!plus) return packages;

  // https://cyans.linkorb.com/topics/6jmFcP4vQcWYF5P5Qjholg
  applyOffer(plus, dimension6) || applyOffer(plus, dimension7);

  return packages;
}

function applyOffer(plus, dimension) {
  switch (dimension) {
    case 'notification-p34':
    case 'offer-79':
      plus.name = 'online-cursussen-offer1';
      plus.newPrice = '89,95';
      return true;
    case 'offer-99':
      plus.name = 'online-cursussen';
      plus.newPrice = '99,95';
      return true;
    default:
      return false;
  }
}
